// @ts-nocheck
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { DataSource } from "@/components/SourceBadge";
import { isIOSNative } from "@/lib/platform";

export type JournalMode = "free" | "reflection" | "gratitude";

export interface JournalEntry {
  id: string;
  mode: JournalMode;
  content: string;
  mood: number | null;
  prompt_used: string | null;
  voice_url: string | null;
  is_shareable: boolean;
  mirror_response: string | null;
  source: DataSource;
  external_id: string | null;
  created_at: string;
}

interface UseJournalReturn {
  entries: JournalEntry[];
  loading: boolean;
  saving: boolean;
  saveEntry: (params: {
    mode: JournalMode;
    content: string;
    mood?: number;
    promptUsed?: string;
    voiceUrl?: string;
    isShareable?: boolean;
    source?: DataSource;
    /** Mirror voice-capture idempotency key — duplicate = replay, not a new row. */
    clientActionId?: string;
  }) => Promise<JournalEntry | null>;
  updateEntry: (id: string, params: { content: string; mood?: number; is_shareable?: boolean }) => Promise<void>;
  deleteEntry: (id: string) => Promise<void>;
  refresh: () => void;
}

export const useJournal = (): UseJournalReturn => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from("journal_entries")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (!error && data) setEntries(data as JournalEntry[]);
    } catch {
      // Table may not exist yet
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const saveEntry = useCallback(async ({
    mode, content, mood, promptUsed, voiceUrl, isShareable = false, source = "manual",
    clientActionId,
  }: {
    mode: JournalMode;
    content: string;
    mood?: number;
    promptUsed?: string;
    voiceUrl?: string;
    isShareable?: boolean;
    source?: DataSource;
    clientActionId?: string;
  }): Promise<JournalEntry | null> => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;

      let mirrorResponse: string | null = null;
      try {
        const { data } = await Promise.race([
          supabase.functions.invoke("mirror-chat", {
            body: {
              promptType: "journal_response",
              userId: session.user.id,
              journalMode: mode,
              journalContent: content.slice(0, 300),
              // Apple 3.1.1 — tells mirror-chat to omit price + checkout URL on iOS native.
              platform: isIOSNative() ? "ios" : "web",
            },
          }),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("timeout")), 4000)
          ),
        ]);
        if (data?.reply) mirrorResponse = (data.reply as string).trim();
      } catch { /* Mirror response is optional */ }

      const row: Record<string, unknown> = {
        user_id: session.user.id,
        mode,
        content,
        mood: mood ?? null,
        prompt_used: promptUsed ?? null,
        voice_url: voiceUrl ?? null,
        is_shareable: isShareable,
        mirror_response: mirrorResponse,
        source,
      };
      if (clientActionId) row.client_action_id = clientActionId;

      const { data: inserted, error } = await supabase
        .from("journal_entries")
        .insert(row)
        .select()
        .single();

      if (error) {
        // 23505 = duplicate client_action_id (voice double-confirm). Idempotent
        // replay: return the existing row so the caller treats it as success.
        if ((error as { code?: string }).code === "23505" && clientActionId) {
          const { data: existing } = await supabase
            .from("journal_entries")
            .select("*")
            .eq("user_id", session.user.id)
            .eq("client_action_id", clientActionId)
            .maybeSingle();
          if (existing) {
            console.info("[useJournal] idempotent replay — returning existing entry");
            return existing as JournalEntry;
          }
        }
        console.error("[useJournal] saveEntry insert failed:", error);
        return null;
      }
      if (!inserted) {
        console.error("[useJournal] saveEntry insert returned no row (RLS post-insert SELECT?)");
        return null;
      }
      setEntries(prev => [inserted as JournalEntry, ...prev]);
      return inserted as JournalEntry;
    } finally {
      setSaving(false);
    }
  }, []);

  const updateEntry = useCallback(async (
    id: string,
    params: { content: string; mood?: number; is_shareable?: boolean }
  ) => {
    const { error } = await supabase
      .from("journal_entries")
      .update({
        content: params.content,
        mood: params.mood ?? null,
        is_shareable: params.is_shareable ?? false,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (!error) {
      setEntries(prev => prev.map(e =>
        e.id === id ? { ...e, content: params.content, mood: params.mood ?? null, is_shareable: params.is_shareable ?? false } : e
      ));
    }
  }, []);

  const deleteEntry = useCallback(async (id: string) => {
    const { error } = await supabase
      .from("journal_entries")
      .delete()
      .eq("id", id);
    if (!error) setEntries(prev => prev.filter(e => e.id !== id));
  }, []);

  return { entries, loading, saving, saveEntry, updateEntry, deleteEntry, refresh: fetchEntries };
};